"use client";

import { HomeType, INTERNAL_LINK, PageType, StrapiMedia } from "@/types";
import Image from "next/image";
import Link from "next/link";
import { Fragment, useEffect, useMemo, useState } from "react";
import styles from "@/styles/Home.module.scss";
import { motion } from "framer-motion";
import { isTouchDevice } from "@/app/common/Utils";
import { LogoIcon } from "./Partials/LogoIcon";
import { RichText } from "./StrapiComponents/RichText";
import { HomePageLink } from "./HomePageLink";
import StrapiImage from "./StrapiComponents/StrapiImage";
import { SkeletonGrid } from "./SkeletonGrid";

export default function PageShuffle({ pages, priority, homeContent, slug = null }: { pages: PageType[]; homeContent: HomeType | null; priority: boolean; slug: string | null }) {
	// const usedPages = pages; //slug !== null ? pages.filter((p) => p.slug !== slug) : pages
	const [clientLoaded, setClientLoaded] = useState(false);

	useEffect(() => {
		setClientLoaded(true);
	}, []);

	const [windowWidth, setWindowWidth] = useState(0);
	const [isTouch, setIsTouch] = useState<boolean>(true);

	const [shuffledPages, setShuffledPages] = useState<PageType[]>([]);
	const [featuredIndexes, setFeaturedIndexes] = useState<number[]>([]);

	// Shuffle-Funktion
	const shuffleArray = (array: PageType[]) => {
		const shuffled = [...array];
		for (let i = shuffled.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
		}
		return shuffled;
	};

	// Wird nur für den shufflePage benötigt
	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		// Resize Listener hinzufügen
		window.addEventListener("resize", handleResize);

		// Cleanup Function
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Shuffle beim ersten Render oder Seiten-Neuladen
	useEffect(() => {
		setShuffledPages(shuffleArray(pages));
	}, [pages]);

	// Berechnung der Featured-Indexes, abhängig von der Fenstergröße
	useEffect(() => {
		if (typeof window === "undefined") return;

		const calculateFeaturedIndexes = () => {
			const newFeatured: number[] = [];

			shuffledPages.forEach((_, index) => {
				let featured = index === 4;
				if (window.innerWidth < 810) {
					featured = false;
				}
				if (window.innerWidth > 810 && window.innerWidth < 1200) {
					featured = index % 5 === 0;
				}

				if (featured) newFeatured.push(index);
			});

			// Nur aktualisieren, wenn sich die Werte wirklich ändern
			if (JSON.stringify(newFeatured) !== JSON.stringify(featuredIndexes)) {
				setFeaturedIndexes(newFeatured);
			}

			setIsTouch(isTouchDevice());
		};

		// Initial berechnen und bei Fenstergröße-Änderung neu berechnen
		calculateFeaturedIndexes();
		window.addEventListener("resize", calculateFeaturedIndexes);
		return () => window.removeEventListener("resize", calculateFeaturedIndexes);
	}, [shuffledPages, windowWidth]); // Abhängig von geshuffelten Seiten

	useEffect(() => {
		console.log(`Running on commit: ${process.env.NEXT_PUBLIC_COMMIT_HASH}`);
	}, []);

	return (
		<>
			{clientLoaded ? (
				shuffledPages.map((page: PageType, index: number) => {
					return (
						<Fragment key={"fragment" + index}>
							{/** CTA Notification */}
							{index === 5 && (
								<motion.div
									className={`${styles.item} ${styles.featured} ${styles.cta_notification}`}
									style={{
										backgroundImage: homeContent?.smartphoneMedia ? `url(${process.env.NEXT_PUBLIC_API_ORIGIN as string}${homeContent.smartphoneMedia.url})` : "url(/static/images/smartphone_cta.png)",
									}}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									key={"feature-" + index}
									transition={{ duration: 0.8 }}
								>
									<div className={styles.itemInner}>
										{homeContent && (
											<>
												<RichText content={homeContent.smartphoneCta} />
												{homeContent.qrImage && (
													<div className={styles.qrImage}>
														<StrapiImage media={homeContent.qrImage} loadingType={"eager"} />
													</div>
												)}
											</>
										)}
									</div>
								</motion.div>
							)}

							{/** ArticleBlock */}
							<motion.div
								className={`${styles.item} ${featuredIndexes.includes(index) ? styles.featured : ""} ${isTouch ? styles.touchDevice : "no-touch-device"}`}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.8 }}
								key={"feature-2-" + index}
							>
								<HomePageLink page={page} />
							</motion.div>

							{/** BUA LOGO */}
							{index === 9 && (
								<motion.div
									className={`${styles.item} ${styles.cta_notification} ${styles.gridLogo}`}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									key={"feature-1-" + index}
									transition={{ duration: 0.8 }}
								>
									<div className={`${styles.itemInner} flex flex-h-center`}>
										<Link href={INTERNAL_LINK.About}>
											<LogoIcon />
										</Link>
									</div>
								</motion.div>
							)}
						</Fragment>
					);
				})
			) : (
				<SkeletonGrid count={12} />
			)}
		</>
	);
}
